/**
 * The seam between two schedule rows.
 *
 * The current-time line runs a few px past the bottom of its own row so it
 * reads as one continuous line across the gap to the row below. Those px are
 * drawn OUTSIDE the row box — and that strip is exactly where the sticky label
 * column's background stops, so a sliver of the marker showed up right next to
 * the stage and custom-subject labels (between two rows, and under the last
 * one) even though the marker itself was scrolled behind the column.
 *
 * The fix is a cover strip on the label column (an `::after` sitting directly
 * below it). Its height MUST be at least the bridge, which is what
 * `scheduleRowSeams.test.ts` pins down — the read-only timeline shipped with a
 * bridge and no cover at all.
 *
 * Two details the strip has to get right:
 *  - it reaches over the label's OWN borders. `left` / `width` on a
 *    pseudo-element resolve against the padding box, so a plain `left-0
 *    w-full` leaves the coloured 4px stage border uncovered — and the marker
 *    keeps peeking through that band.
 *  - it paints in the colour of whatever it covers. In the read-only view the
 *    strip lands exactly on the row's 1px `border-b`; filling it with the page
 *    background would erase the divider between two rows, so the strip IS the
 *    divider.
 *
 * The bridge is owned by the marker (`CurrentTimeMarkerLine`); the gap it
 * bridges is owned by the row: `mb-2` on the creator's row wrapper, `border-b`
 * on the read-only row. Change either of those and these numbers move with it.
 *
 * Values are literal Tailwind classes (not computed) so the JIT compiler picks
 * them up.
 */

/** Shared positioning; each view adds its own size, offset and colour. */
export const LABEL_SEAM_COVER = "after:absolute after:top-full after:pointer-events-none";

export const ROW_SEAM = {
  /**
   * Schedule creator: rows are 8px apart (`mb-2`) and the label has no
   * coloured left border, so the strip simply fills the gap in card colour.
   */
  creator: {
    bridgePx: 8,
    coverPx: 8,
    coverClass: "after:left-0 after:h-2 after:w-full after:bg-card",
  },
  /**
   * Read-only / client-share timeline: rows sit flush, separated by the row's
   * 1px `border-b`. The strip redraws that divider and reaches 4px to the left
   * to clear the coloured stage / crew border on the label.
   */
  readOnly: {
    bridgePx: 1,
    coverPx: 1,
    coverClass: "after:-left-1 after:h-px after:w-[calc(100%+4px)] after:bg-border",
  },
} as const;
